import { z } from 'zod';
import { err, ok, validate, validationError, type Result } from '../errors/errors';
import { BallotSchema, FlowCellSchema, FlowRowSchema, RoundSchema, SettingsSchema, TimerRecordSchema, TournamentSchema, type Settings } from '../schemas/entities';

/**
 * Sync is a ledger of change events, one per write or delete, each owned by one account.
 *
 * - Devices append events for their own changes and pull everyone else's. The server stores them in order (`seq`).
 * - Per record, the last event wins: latest `at` (the device clock when the change was made), then `id` on a tie.
 * - A delete is an event too (a tombstone). It stays in the ledger, so a device that was offline still learns about
 *   it; the device's live tables really remove the record.
 * - Debater names are not a kind: they can't enter the ledger.
 */
export const LEDGER_VERSION = 1;

export const SYNC_KINDS = ['tournament', 'round', 'row', 'cell', 'timer', 'ballot', 'settings'] as const;
export type SyncKind = (typeof SYNC_KINDS)[number];

/** Settings that stay on each device: which tournament it last opened, and its text size (iPad ≠ laptop). */
export const DEVICE_SETTINGS = ['currentTournamentId', 'flowFontScale'] as const;
export const SyncedSettingsSchema = SettingsSchema.omit({ currentTournamentId: true, flowFontScale: true });
export type SyncedSettings = z.infer<typeof SyncedSettingsSchema>;

const KIND_SCHEMAS: Record<SyncKind, z.ZodType> = {
  tournament: TournamentSchema,
  round: RoundSchema,
  row: FlowRowSchema,
  cell: FlowCellSchema,
  timer: TimerRecordSchema,
  ballot: BallotSchema,
  settings: SyncedSettingsSchema,
};

const KIND_NAMES: Record<SyncKind, string> = { tournament: 'tournament', round: 'round', row: 'flow row', cell: 'flow box', timer: 'timer', ballot: 'ballot', settings: 'settings' };

/** A record's id within its kind: `id`, or `roundId` for the per-round timer and ballot, or "settings". */
export const recordIdOf = (kind: SyncKind, data: unknown): string | undefined => {
  const r = data as Record<string, unknown> | null;
  const v = kind === 'timer' || kind === 'ballot' ? r?.roundId : r?.id;
  return typeof v === 'string' ? v : undefined;
};

const timestamp = z.number().int().nonnegative();

export const LedgerEventSchema = z.object({
  v: z.literal(LEDGER_VERSION, { error: 'This change comes from a newer version of flow. Update the app.' }),
  /** Unique per event (made on the device). Also the tie-break when two events have the same `at`. */
  id: z.string().min(8).max(64),
  /** The account that owns the change. Events never cross accounts. */
  accountId: z.string().min(1).max(80),
  /** The device (browser profile) that made the change. */
  deviceId: z.string().min(1).max(64),
  /** When the change was made, by the device's clock (ms). */
  at: timestamp,
  kind: z.enum(SYNC_KINDS, { error: (i) => `"${String(i.input)}" can't be synced.${i.input === 'names' ? ' Debater names stay on the device.' : ''}` }),
  recordId: z.string().min(1).max(160),
  op: z.enum(['put', 'delete']),
  /** The whole record after the change (put only). */
  data: z.unknown().optional(),
  /** Order on the server, set when it stores the event. */
  seq: z.number().int().positive().optional(),
});
export type LedgerEvent = z.infer<typeof LedgerEventSchema>;

/**
 * Check an event before it's stored or applied: the envelope, the owner, and (for a put) the record itself, with
 * the same rules the app uses when saving it. Every problem is reported at once, in plain language.
 */
export const validateEvent = (raw: unknown, accountId: string): Result<LedgerEvent> => {
  const env = validate(LedgerEventSchema, raw);
  if (!env.ok) return env;
  const e = env.value;
  const issue = (path: string, message: string) => err(validationError([{ path, code: 'custom', message }]));
  if (e.accountId !== accountId) return issue('accountId', 'This change belongs to a different account.');
  if (e.op === 'delete') return e.data === undefined ? ok(e) : issue('data', 'A delete carries no record.');
  const rec = validate(KIND_SCHEMAS[e.kind], e.data);
  if (!rec.ok) {
    return rec.error.kind === 'validation'
      ? err(validationError(rec.error.issues.map((i) => ({ ...i, path: i.path ? `data.${i.path}` : 'data', message: `In this ${KIND_NAMES[e.kind]}: ${i.message}` }))))
      : rec;
  }
  if (recordIdOf(e.kind, rec.value) !== e.recordId) return issue('recordId', `The ${KIND_NAMES[e.kind]} doesn't match the id it was sent under.`);
  return ok({ ...e, data: rec.value });
};

/** Identifies a record across kinds, e.g. "cell:r1~C1". */
export const recordKey = (kind: SyncKind, recordId: string) => `${kind}:${recordId}`;

/** The newest-first order: later `at` wins; on a tie, the larger event id (any fixed rule works, as long as every device uses it). */
export const isNewer = (a: Pick<LedgerEvent, 'at' | 'id'>, b: Pick<LedgerEvent, 'at' | 'id'>) => a.at > b.at || (a.at === b.at && a.id > b.id);

/** The winning event per record. */
export const latestPerRecord = (events: LedgerEvent[]): Map<string, LedgerEvent> => {
  const out = new Map<string, LedgerEvent>();
  for (const e of events) {
    const k = recordKey(e.kind, e.recordId);
    const cur = out.get(k);
    if (!cur || isNewer(e, cur)) out.set(k, e);
  }
  return out;
};

/** What a device knows about a record: the event its live copy came from. */
export type Head = Pick<LedgerEvent, 'at' | 'id'>;

/**
 * Which incoming events a device should apply to its live tables: per record, the newest incoming event, if it's
 * newer than the device's head for that record. Duplicates and stale events are skipped.
 */
export const planApply = (heads: ReadonlyMap<string, Head>, incoming: LedgerEvent[]): { apply: LedgerEvent[]; skipped: number } => {
  const apply: LedgerEvent[] = [];
  for (const [k, e] of latestPerRecord(incoming)) {
    const head = heads.get(k);
    if (!head || isNewer(e, head)) apply.push(e);
  }
  return { apply, skipped: incoming.length - apply.length };
};

/** The event for a change made on this device. */
export const ledgerEvent = (
  ctx: { accountId: string; deviceId: string; now: number; newId: () => string },
  kind: SyncKind,
  change: { op: 'put'; data: unknown } | { op: 'delete'; recordId: string },
): LedgerEvent => {
  const recordId = change.op === 'put' ? recordIdOf(kind, change.data) : change.recordId;
  if (!recordId) throw new Error(`No id on this ${KIND_NAMES[kind]}`);
  const data = change.op === 'put' && kind === 'settings' ? syncedSettings(change.data as Settings) : change.op === 'put' ? change.data : undefined;
  return { v: LEDGER_VERSION, id: ctx.newId(), accountId: ctx.accountId, deviceId: ctx.deviceId, at: ctx.now, kind, recordId, op: change.op, ...(data === undefined ? {} : { data }) };
};

/** The part of settings that syncs (device-only fields removed). */
export const syncedSettings = (s: Settings): SyncedSettings => {
  const { currentTournamentId: _t, flowFontScale: _f, ...rest } = s;
  return rest;
};

/** Settings from another device, applied here: this device keeps its own device-only fields. */
export const mergeSettings = (local: Settings, synced: SyncedSettings): Settings => ({ ...local, ...syncedSettings({ ...local, ...synced }) });
